import React, { useState, useEffect } from 'react';
import { Typography, Alert, List } from 'antd';

import { getTours } from '../services/toursService';

function TourTypeIcon({ type }) {
  switch (type) {
    case 'walking':
      return <div />;
    case 'cycling':
      return <div />;
    case 'driving':
      return <div />;
    default:
      return <div />;
  }
}

function formatDuration(duration) {
  if (!duration) return 'Duration not specified';

  // Handle PostgreSQL interval format which comes as an object or string
  try {
    if (typeof duration === 'string') {
      // Try to parse "HH:MM:SS" format
      const [hours, minutes] = duration.split(':');
      const durationText = [];
      if (parseInt(hours) > 0) durationText.push(`${parseInt(hours)}h`);
      if (parseInt(minutes) > 0) durationText.push(`${parseInt(minutes)}m`);
      return durationText.join(' ') || 'Duration not specified';
    } else {
      // Handle object format {hours: X, minutes: Y}
      const durationText = [];
      if (duration.hours > 0) durationText.push(`${duration.hours}h`);
      if (duration.minutes > 0) durationText.push(`${duration.minutes}m`);
      return durationText.join(' ') || 'Duration not specified';
    }
  } catch (error) {
    console.error('Error formatting duration:', error);
    return 'Duration not specified';
  }
}

function TourCard({ tour }) {
  return (
    <div
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '4px solid black',
        borderRadius: 6,
      }}
    >
      <div
        component="img"
        height="200"
        image={tour.image_url || 'https://source.unsplash.com/random?tour'}
        alt={tour.name}
      />
      <div sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          {tour.name}
        </Typography>

        <div sx={{ mb: 2 }}>
          <div
            icon={<TourTypeIcon type={tour.type} />}
            label={tour.type}
            size="small"
            sx={{ mb: 1, mr: 1 }}
          />
          {tour.difficulty && (
            <div
              icon={<div />}
              label={`Difficulty: ${tour.difficulty}/5`}
              size="small"
              sx={{ mb: 1 }}
            />
          )}
        </div>

        <Typography variant="body2" color="text.secondary" paragraph>
          {tour.description}
        </Typography>

        <div sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <div sx={{ mr: 1, fontSize: 20 }} color="action" />
          <Typography variant="body2">{formatDuration(tour.duration)}</Typography>
        </div>

        {tour.distance && (
          <div sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <div sx={{ mr: 1, fontSize: 20 }} color="action" />
            <Typography variant="body2">{tour.distance} km</Typography>
          </div>
        )}

        {tour.tour_stops && tour.tour_stops.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Stops:
            </Typography>
            <List dense>
              {tour.tour_stops
                .sort((a, b) => a.stop_number - b.stop_number)
                .map(stop => (
                  <div key={stop.id} sx={{ px: 0 }}>
                    <div sx={{ minWidth: 36 }}>
                      <div color="action" />
                    </div>
                    <div primary={stop.place.name} secondary={stop.description} />
                  </div>
                ))}
            </List>
          </>
        )}
      </div>
    </div>
  );
}

function Tours() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const data = await getTours();
        setTours(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, []);

  if (loading) {
    return (
      <div sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <div />
      </div>
    );
  }

  if (error) {
    return (
      <div sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <div sx={{ px: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Tours
      </Typography>

      {tours.length === 0 ? (
        <Typography>No tours found.</Typography>
      ) : (
        <div container spacing={4}>
          {tours.map(tour => (
            <div item key={tour.id} xs={12} sm={6} md={4}>
              <TourCard tour={tour} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Tours;
